import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, CheckCircle2 } from "lucide-react";
import SectionTitle from "../components/SectionTitle";
import ServiceCard from "../components/ServiceCard";
import FloatingPetals from "../components/FloatingPetals";
import Reveal from "../components/Reveal";
import BookLink from "../components/BookLink";
import useSeo from "../hooks/useSeo";
import { services, business, images } from "../data/content";

export default function Services() {
  useSeo({
    title: `Services | Soft Petal Electrolysis, ${business.city}, ${business.state}`,
    description: `Gentle, precise electrolysis in ${business.city}, ${business.state}. Facial and body permanent hair removal, personalized treatment plans, and complimentary consultations for every skin tone.`,
    path: "/services",
  });

  const areas = [
    "Upper lip & chin", "Jawline & cheeks", "Sideburns & brow",
    "Underarms", "Bikini line", "Stomach & chest",
    "Back & shoulders", "Fingers & toes", "Hormonal hair growth",
  ];

  const process = [
    { title: "Consultation", copy: "A warm conversation about your goals, skin, and treatment options." },
    { title: "Personalized plan", copy: "A gentle roadmap tailored to your area, hair-growth cycle, and comfort." },
    { title: "Your sessions", copy: "Focused, unhurried appointments in a calm, judgment-free space." },
    { title: "Aftercare", copy: "Clear guidance so your skin can settle beautifully between visits." },
  ];

  return (
    <div>
      <section className="watercolor-blush py-16 lg:py-14 relative overflow-hidden">
        <FloatingPetals count={6} />
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <SectionTitle
            eyebrow="Services"
            title="Gentle. Precise."
            scriptWord="Permanent."
            subtitle="Electrolysis is currently the only FDA-recognized method of permanent hair removal, and it works beautifully across all skin tones and hair colors."
          />
        </div>
      </section>

      {/* Welcoming portrait band */}
      <section className="pt-12 lg:pt-16 pb-6 lg:pb-8" data-testid="services-intro-portrait">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6">
            <div className="relative">
              <div className="rounded-[2.5rem] overflow-hidden aspect-[4/5] shadow-soft">
                <img
                  src={images.servicesIntro}
                  alt="A Soft Petal Pal glowing at sunset with a pink lily in her hair"
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out hover:scale-[1.04]"
                />
              </div>
              <div className="absolute -bottom-5 -right-5 w-24 h-24 rounded-full bg-blush flex items-center justify-center shadow-soft float-gentle">
                <div className="text-center">
                  <div className="font-script text-petal text-xl leading-none">smooth</div>
                  <div className="font-script text-petal text-xl leading-none">& seen</div>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={140} className="lg:col-span-6">
            <SectionTitle
              eyebrow="Welcome"
              title="Care that meets"
              scriptWord="you where you are"
              align="left"
            />
            <p className="mt-6 font-body text-lg text-charcoal leading-relaxed">
              Every Soft Petal Pal walks in with their own story, their own goals, and their own
              rhythm. Our services are designed around <span className="text-petal font-medium">you</span>,
              your skin, your hair, and your comfort, so every appointment feels warm,
              intentional, and unmistakably yours.
            </p>
            <p className="mt-5 font-body text-charcoal-soft leading-relaxed">
              Here&rsquo;s what electrolysis is, how it works, and what to expect from your journey.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16" data-testid="what-is-section">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <h3 className="font-serif-display text-3xl leading-tight">What is electrolysis?</h3>
            <p className="mt-5 font-body text-charcoal-soft leading-relaxed">
              Electrolysis is a method of permanent hair removal that treats hair one follicle at a time. A fine, sterile probe is placed alongside the hair, and a small amount of energy disables future growth in that follicle.
            </p>
            <h3 className="font-serif-display text-3xl leading-tight mt-10">How it works</h3>
            <p className="mt-5 font-body text-charcoal-soft leading-relaxed">
              Because hair grows in cycles, we treat the same area across several sessions to catch each follicle at the ideal stage. Over time, treated follicles stop producing new hair.
            </p>
          </div>
          <div className="bg-blush/50 rounded-3xl p-8 sm:p-10 border border-pale-rose/50 shadow-soft" data-testid="areas-treated">
            <div className="flex items-center gap-2 text-petal font-body text-xs tracking-[0.3em] uppercase">
              <Sparkles size={14} /> Areas we treat
            </div>
            <ul className="mt-6 grid grid-cols-2 gap-x-4 gap-y-3">
              {areas.map((a) => (
                <li key={a} className="flex items-start gap-2 font-body text-sm text-charcoal">
                  <CheckCircle2 size={16} className="text-petal shrink-0 mt-0.5" />
                  {a}
                </li>
              ))}
            </ul>
            <p className="mt-8 font-body text-sm text-charcoal-soft italic border-t border-pale-rose/60 pt-6">
              Electrolysis is suitable for all skin tones and can treat a wide range of hair colors and textures. Individual treatment timelines vary based on the area, hair-growth cycle, consistency of appointments, and personal goals.
            </p>
          </div>
        </div>
      </section>

      {/* SERVICE CARDS */}
      <section className="py-14 lg:py-18 bg-blush/40" data-testid="services-list">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <SectionTitle
            eyebrow="Our Menu"
            title="Choose the session that"
            scriptWord="feels right"
          />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-7">
            {services.map((s, i) => (
              <Reveal key={s.slug} delay={i * 110}>
                <ServiceCard service={s} testIdPrefix="services-page" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-14 lg:py-18" data-testid="process-section">
        <div className="max-w-6xl mx-auto px-6 sm:px-10">
          <SectionTitle
            eyebrow="What to Expect"
            title="Your Soft Petal"
            scriptWord="journey"
          />
          <ol className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-7">
            {process.map((step, i) => (
              <Reveal
                as="li"
                key={step.title}
                delay={i * 110}
                className="relative bg-warm-white rounded-3xl p-7 border border-pale-rose/50 shadow-soft lift-on-hover"
                data-testid={`process-step-${i}`}
              >
                <div className="w-10 h-10 rounded-full bg-petal text-white font-serif-display flex items-center justify-center text-lg">
                  {i + 1}
                </div>
                <h3 className="mt-5 font-serif-display text-xl leading-tight">{step.title}</h3>
                <p className="mt-2 font-body text-sm text-charcoal-soft leading-relaxed">{step.copy}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* PREPARE & AFTERCARE */}
      <section className="py-14 lg:py-18 bg-blush/40">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 grid lg:grid-cols-2 gap-8">
          <div className="bg-warm-white rounded-3xl p-10 shadow-soft border border-pale-rose/40">
            <h3 className="font-serif-display text-2xl">Preparing for treatment</h3>
            <ul className="mt-5 space-y-3 font-body text-sm text-charcoal-soft">
              <li>· Arrive with clean, product-free skin.</li>
              <li>· Stay hydrated and avoid excessive sun the day of your visit.</li>
              <li>· Do not tweeze, wax, or thread, shaving is completely fine.</li>
              <li>· Let us know about any recent skin treatments or medications.</li>
            </ul>
          </div>
          <div className="bg-warm-white rounded-3xl p-10 shadow-soft border border-pale-rose/40">
            <h3 className="font-serif-display text-2xl">Aftercare guidance</h3>
            <ul className="mt-5 space-y-3 font-body text-sm text-charcoal-soft">
              <li>· Temporary redness and mild sensitivity are normal.</li>
              <li>· Keep the area clean and avoid heavy makeup on treated skin that day.</li>
              <li>· Skip saunas, hot tubs, and intense workouts for 24 hours.</li>
              <li>· Apply gentle SPF when outdoors.</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <SectionTitle title="Let&rsquo;s design your" scriptWord="plan together." />
          <div className="mt-10">
            <BookLink  className="btn-petal" data-testid="services-cta-book">
              Book a Consultation
              <ArrowRight size={14} />
            </BookLink>
          </div>
        </div>
      </section>
    </div>
  );
}
